import { APIResponseType } from 'app/apiService/types';
import apiService from '..';

interface RunLog {
  _id: string;
  name: string;
  initial_parameters: any;
  dealer_id: string;
  run_status_message?: string;
  actions?: Array<{
    _id: string;
    function_name: string;
    parameters: any;
    status: string;
    status_history: Array<{
      status: string;
      timestamp: string;
      reason?: string;
      _id: string;
    }>;
    metadata: any;
    is_data_available_to_download: boolean;
  }>;
  status: string;
  has_retryable_records?: boolean;
  run_version: string;
  run_type: string;
  run_by: string;
  run_by_id: string;
  created_at: string;
  updated_at: string;
}

interface RunLogsResponse {
  data: RunLog[];
  meta: {
    totalDocuments: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
  };
}

class RunLogs {
  static ENDPOINT = 'api/v2/run-v2';

  static async getAllRunLogs(params: {
    dealer?: string;
    skip?: number;
    limit?: number;
    filters?: string;
    sort?: string;
  }): Promise<APIResponseType<RunLogsResponse>> {
    try {
      const response = await apiService.get<RunLogsResponse>(`${this.ENDPOINT}`, params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async getRunByRunId(runId: string): Promise<APIResponseType<RunLog>> {
    if (!runId) throw new Error("Run ID is required");
    try {
      const response = await apiService.get<RunLog>(`${this.ENDPOINT}/${runId}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async createRunLog(payload: any): Promise<APIResponseType<RunLog>> {
    try {
      const response = await apiService.post<RunLog>(`${this.ENDPOINT}`, payload);
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async updateRunLog(runId: string, payload: Partial<RunLog>): Promise<APIResponseType<RunLog>> {
    if (!runId) throw new Error("Run ID is required for updating");
    if (!Object.keys(payload).length) throw new Error("Update payload cannot be empty");

    try {
      const response = await apiService.put<RunLog>(`${this.ENDPOINT}/${runId}`, payload);
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async deleteRunLog(runId: string): Promise<APIResponseType> {
    if (!runId) throw new Error("Run ID is required for deletion");
    try {
      const response = await apiService.delete<APIResponseType>(`${this.ENDPOINT}/${runId}`);
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async downloadActionData(runId: string, actionId: string, format: string = 'json'): Promise<string> {
    if (!runId) throw new Error("Run ID is required for download");
    if (!actionId) throw new Error("Action ID is required for download");

    try {
      const downloadUrl = `${process.env.REACT_APP_API_URL}/${this.ENDPOINT}/${runId}/actions/${actionId}/download?format=${format}`;
      return downloadUrl;
    } catch (error) {
      throw error;
    }
  }
}

export default RunLogs;
export type { RunLog, RunLogsResponse }; 